import {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDevice} from '@lookiero/aurora';
import {setStringAsync as clipboardSetStringAsync} from 'expo-clipboard';

import {LocationApplication} from '@application/Site';
import {eventManager} from '@application/Event';
import {NavigationEvent} from '@domain/model/Event';

import {inAppBrowser} from '@helpers';
import {useOverallInfo, useToast} from '@components/_context';
import {openExternalLink} from '@router/helpers';

import {shareFacebook} from '../helpers';
import {Section} from '@views/Referrals';

const CHANNEL = {
  CLIPBOARD: 'clipboard',
  EMAIL: 'email',
  FACEBOOK: 'facebook',
  WHATSAPP: 'whatsapp',
} as const;
type Channel = (typeof CHANNEL)[keyof typeof CHANNEL];

const TEXT = {
  EMAIL: {id: 'mgm.share.email'},
  FACEBOOK: {id: 'mgm.share.fb'},
  LINK: {id: 'mgm.copy.link'},
  WHATS: {id: 'mgm.share.wats'},

  SHARE_LINK: {id: 'mgm.share.link.ok'},
  EMAIL_BODY: {id: 'mgm.email.body'},
  EMAIL_SUBJECT: {id: 'mgm.email.subject'},
  WHATS_BODY: {id: 'mgm.whats.body'},
} as const;
const addSourceToReferralLink = (referralLink: string, source: string): string => `${referralLink}&mgm=${source}`;

const getReferralLink = (referralCode: string): string =>
  `${LocationApplication.getHostname()}/referrals-landing?code=${referralCode}`;

type ShareReferral = (source: Channel, section: Section) => void | Promise<void>;

type useReferralLinkProps = {
  referralCode: string;
  referredCredit: number;
};
const useReferralLink = ({
  referralCode,
  referredCredit,
}: useReferralLinkProps): {shareReferral: ShareReferral; referralLink: string} => {
  const {id: userId} = useOverallInfo();
  const {screen} = useDevice();
  const {formatMessage} = useIntl();
  const {showSuccess} = useToast();

  const [referralLink, setReferralLink] = useState('');

  useEffect(() => {
    if (referralCode) {
      setReferralLink(getReferralLink(referralCode));
    }
  }, [referralCode]);

  const shareViaClipboard = async (referralLinkWithSource: string): Promise<void> => {
    await clipboardSetStringAsync(referralLinkWithSource);

    showSuccess({
      message: formatMessage(TEXT.SHARE_LINK),
      settings: {nativeID: 'copy-to-clipboard-toast-success', testID: 'copy-to-clipboard-toast-success'},
    });
  };

  const shareViaEmail = (referralLinkWithSource: string): void => {
    const subject = encodeURIComponent(formatMessage(TEXT.EMAIL_SUBJECT, {referredCredit}));
    const body = encodeURIComponent(
      formatMessage(TEXT.EMAIL_BODY, {
        referredCredit,
        referralLink: referralLinkWithSource,
      }),
    );
    const mailUrl = `mailto:?subject=${subject}&body=${body}`;

    void openExternalLink({url: mailUrl, sameWindow: inAppBrowser});
  };

  const shareViaFacebook = async (referralLinkWithSource: string): Promise<void> => {
    await shareFacebook(referralLinkWithSource);
  };

  const shareViaWhatsapp = (referralLinkWithSource: string): void => {
    const message = encodeURIComponent(
      formatMessage(TEXT.WHATS_BODY, {
        referredCredit,
        referralLink: referralLinkWithSource,
      }),
    );
    const whatsappUrl = `https://${screen.S ? 'api' : 'web'}.whatsapp.com/send?text=${message}`;

    void openExternalLink({url: whatsappUrl, createLink: screen.S, sameWindow: inAppBrowser});
  };

  const SHARE_SOURCE = {
    [CHANNEL.CLIPBOARD]: {func: shareViaClipboard, param: 'c'},
    [CHANNEL.EMAIL]: {func: shareViaEmail, param: 'e'},
    [CHANNEL.FACEBOOK]: {func: shareViaFacebook, param: 'f'},
    [CHANNEL.WHATSAPP]: {func: shareViaWhatsapp, param: 'w'},
  };

  const shareReferral = (source: Channel, section: Section): void | Promise<void> => {
    eventManager.emit(NavigationEvent.REFERRAL_CODE_SHARE, {
      userId,
      section,
      source,
    });
    const referralLinkWithSource = addSourceToReferralLink(referralLink, SHARE_SOURCE[source].param);
    SHARE_SOURCE[source].func(referralLinkWithSource);
  };

  return {referralLink, shareReferral};
};

export {useReferralLink};
